<template>
  <div class="inventory-side-wrapper">
    <SideDrawer
      v-model="model"
      title="Seu inventário de itens"
      hideFooter
      @cancel="close()"
    >
      <template v-slot:content>
        <v-list v-if="_user?.inventory?.items?.length" class="transparent" nav>
          <v-list-item
            v-for="(item, i) in _user?.inventory?.items"
            class="rounded-xl neutral1"
            :key="i"
          >
            <v-list-item-avatar class="mr-2" color="neutral2" rounded>
              <v-img
                v-if="item.item.image"
                :src="item.item.image"
                class="rounded-xl"
                :aspect-ratio="4 / 4"
              />

              <v-icon v-else small> fas fa-ticket-alt </v-icon>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title>{{ item.item.name }}</v-list-item-title>
              <v-list-item-subtitle>
                x{{ item.qty }} unidades
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-btn
                class="btn"
                color="primary"
                :x-small="$vuetify.breakpoint.xsOnly"
                :small="$vuetify.breakpoint.smAndUp"
                @click="handleUseItem(item)"
              >
                {{ item.item.itemType === "product" ? "Receber" : "Usar" }}
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <div v-else class="text-body-2 neutral8--text text-center mt-8">
          Nenhum item foi encontrado em seu inventário
        </div>

        <CustomDialog
          v-model="useDialog"
          ref="customDialog"
          width="400"
          :title="`Inventário: ${selectedItem?.item?.name}`"
          :loading="loading"
          hideFooter
          @cancel="closeUse()"
        >
          <div v-if="selectedItem">
            <v-row align="center" justify="center">
              <v-col v-if="selectedItem.item.image" cols="8">
                <v-img
                  :src="selectedItem.item.image"
                  :aspect-ratio="4 / 4"
                  class="rounded-xl"
                />
              </v-col>

              <v-col cols="12">
                <div class="d-flex text-body-1 font-weight-bold">
                  Escolha quantos você quer utilizar agora
                </div>

                <div class="d-flex text-body-2 neutral8--text mb-4">
                  Você possui x{{ selectedItem?.qty }} unidades
                </div>

                <v-card class="rounded-xl mb-1" color="neutral2">
                  <div class="d-flex align-center">
                    <v-btn
                      color="neutral4"
                      :disabled="loading || useQuantity <= 1"
                      depressed
                      rounded
                      @click="decrease()"
                    >
                      <v-icon>mdi-minus</v-icon>
                    </v-btn>

                    <v-spacer />

                    <span class="text-h6 fill-width">
                      <input
                        v-model.number="useQuantity"
                        class="quantity-input"
                        type="number"
                        min="1"
                        @keypress="preventOnlyNumber"
                      />
                    </span>

                    <v-spacer />

                    <v-btn
                      color="neutral4"
                      :disabled="
                        loading || useQuantity >= (selectedItem?.qty || 0)
                      "
                      depressed
                      rounded
                      @click="increase()"
                    >
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </v-card>

                <div v-if="!valid" class="text-body-2 red--text">
                  <span v-if="!useQuantity">
                    Informe uma quantidade superior à 0
                  </span>

                  <span v-if="useQuantity > selectedItem?.qty">
                    Você não possui essa quantidade
                  </span>
                </div>

                <v-chip-group column>
                  <v-chip
                    v-for="(item, i) in suggestions"
                    :key="i"
                    :disabled="loading"
                    outlined
                    dense
                    @click="setSuggestion(item)"
                  >
                    +{{ item }}
                  </v-chip>
                </v-chip-group>

                <v-row class="mt-6">
                  <v-col cols="3" md="5">
                    <v-btn
                      class="btn font-weight-black"
                      color="white"
                      :disabled="loading"
                      outlined
                      rounded
                      plain
                      block
                      text
                      @click="closeUse()"
                    >
                      <v-icon v-if="$vuetify.breakpoint.smAndDown">
                        mdi-arrow-left
                      </v-icon>

                      <span v-else> Cancelar</span>
                    </v-btn>
                  </v-col>

                  <v-col cols="9" md="7">
                    <v-btn
                      class="btn font-weight-black"
                      color="primary"
                      :loading="loading"
                      :disabled="!valid"
                      rounded
                      block
                      @click="submit()"
                    >
                      Confirmar
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </div>
        </CustomDialog>

        <TokenDialog ref="tokenDialog" />

        <ScratchDialog ref="scratchDialog" />

        <BoxDialog ref="boxDialog" />
      </template>
    </SideDrawer>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { useItem } from "@/services/items";
import { preventOnlyNumber } from "@/helpers";
import SideDrawer from "@/components/sideDrawer/SideDrawer.vue";
import CustomDialog from "@/components/customDialog/CustomDialog";
import TokenDialog from "@/modules/main/partials/tokenDialog/TokenDialog.vue";
import ScratchDialog from "@/modules/main/partials/scratchDialog/ScratchDialog.vue";
import BoxDialog from "@/modules/main/partials/boxDialog/BoxDialog";

export default {
  name: "InventorySide",

  components: {
    SideDrawer,
    CustomDialog,
    TokenDialog,
    ScratchDialog,
    BoxDialog,
  },

  data() {
    return {
      loading: false,
      model: false,
      useDialog: false,
      valid: true,
      selectedItem: null,
      suggestions: [5, 10, 50, 100, 1000],
      useQuantity: 1,
    };
  },

  computed: {
    ...mapGetters({ _user: "user", _currentRaffle: "currentRaffle" }),
  },

  watch: {
    useQuantity() {
      this.valid =
        this.useQuantity > 0 &&
        this.useQuantity <= (this.selectedItem?.qty || 0);
    },
  },

  methods: {
    handleUseItem(data) {
      if (data.item.itemType === "product") {
        this.$router.push(
          {
            name: "Shipping",
            params: { productID: data.item._id },
          },
          () => {},
          () => {}
        );

        return;
      }

      this.selectedItem = data;
      this.useQuantity = 1;
      this.useDialog = true;
      this.valid = true;
    },

    submit() {
      if (!this.valid) return;

      if (this.selectedItem.item.itemClass === "scratch") {
        const payload = {
          item: this.selectedItem.item,
          qty: this.useQuantity,
        };

        this.$refs.scratchDialog.open(payload);
        this.closeUse();

        return;
      }

      if (this.selectedItem.item.itemClass === "box") {
        const payload = {
          item: this.selectedItem.item,
          qty: this.useQuantity,
        };

        this.$refs.boxDialog.open(payload);
        this.closeUse();

        return;
      }

      this.useItem();
    },

    async useItem() {
      if (!this._currentRaffle) return this.$root.$emit("paused-draw");

      try {
        this.loading = true;

        const payload = {
          ...(this.selectedItem?.item?.itemType !== "points" && {
            target: "raffles",
            targetID: this._currentRaffle?._id,
          }),
          qty: this.useQuantity,
        };

        const { data } = await useItem(this.selectedItem.item._id, payload);

        if (this.selectedItem?.item?.itemType !== "points")
          this.$refs.tokenDialog.open(data);

        this.closeUse();

        this.$alert("Item usado com sucesso", "success");
        this.$root.$emit("load-session");
      } catch (error) {
        this.$alert(error?.message, "error");
        this.closeUse();
      } finally {
        this.loading = false;
      }
    },

    closeUse() {
      this.useDialog = false;
      this.valid = false;
      this.selectedItem = null;
      this.useQuantity = 1;
    },

    decrease() {
      if (this.useQuantity === 1) return;

      this.useQuantity--;
    },

    increase() {
      if (this.useQuantity >= this.selectedItem?.qty) return;

      this.useQuantity++;
    },

    setSuggestion(value) {
      const prevision = this.useQuantity + value;

      if (prevision >= this.selectedItem?.qty) return;

      this.useQuantity += prevision;
    },

    open() {
      this.model = true;
    },

    close() {
      this.model = false;
    },

    preventOnlyNumber,
  },
};
</script>

<style src="./style.scss" lang="scss" scoped />
